import React, { useCallback } from "react";
import List from "@material-ui/core/List";
import { Box, Grid, makeStyles, IconButton, Divider, Typography } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import { Wrapper } from "./styled";
import { useDispatch, useSelector } from "react-redux";
import Preloader from "../../Common/Preloader";
import { useHistory, useLocation } from "react-router-dom";
import { fetchTableSpin, tableSelector, tableSpinSuccess } from "../../../redux/tableReducer";
import { prettyNum } from "../../../utils/prettyNum";
import { gameSelector, getGame } from "../../../redux/gameReducer";
import { useTranslation } from "react-i18next";
import { convertUTCDateToLocalDate } from "../../../utils/date";
import Combination from "./Combination";

const useStyles = makeStyles(() => ({
    closeBtn: {
        position: "absolute",
        top: "0",
        right: "0",
    },
    card: {
        width: "40px",
        height: "60px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        margin: "4px",
        borderRadius: "4px",
        fontWeight: "bold",
        backgroundColor: "#fff",
    }
}));

const getNominal = (cardNum: number): number => {
    return cardNum < 52 ? cardNum % 13 : 13;
};

const getFace = (cardNum: number): string => {
    const nominal = getNominal(cardNum);
    if (nominal < 9) {
        return String(nominal + 2);
    } else if (nominal < 13) {
        return ["J", "Q", "K", "A"][nominal - 9];
    }
    return "*";
};

const getColor = (cardNum: number): number => {
    return cardNum < 26 ? 0 : 1;
};

const getSuitSymbol = (cardNum: number): string => {
    const suit = Math.floor(cardNum / 13);
    return ["♠", "♣", "♦", "♥"][suit];
};

interface SpinProps {
    spinId: number;
    hide: () => void;
}

const Spin: React.FC<SpinProps> = ({ spinId, hide }) => {
    const dispatch = useDispatch();
    const classes: any = useStyles();
    const history = useHistory();
    const location = useLocation();
    const [t] = useTranslation();

    const {
        spin: { data: spin, loading },
        sessions: { data: sessions },
    } = useSelector(tableSelector);
    const { game } = useSelector(gameSelector);

    React.useEffect(() => {
        if (spinId) {
            dispatch(fetchTableSpin(spinId, 1, 20));
        }
    }, [dispatch, spinId]);

    React.useEffect(() => {
        return () => {
            dispatch(tableSpinSuccess(null));
        };
    }, [dispatch]);

    const parseWins = spin?.wins ? JSON.parse(spin?.wins) : null;
    const session_id = new URLSearchParams(location.search).get("session_id");
    const gameId = sessions?.find((i: any) => i.id === Number(session_id))?.game.id;

    React.useEffect(() => {
        if (gameId && gameId !== game?.id) {
            dispatch(getGame(gameId));
        }
    }, [dispatch, gameId, game?.id]);

    const renderCard = useCallback((cardNum: number, isChosen: boolean = false, dealerCardNum: number = -1) => {
        console.log(cardNum, isChosen, dealerCardNum);
        const isRed = getColor(cardNum) === 1;
        let borderColor = "#ccc";
        let borderWidth = "1px";
        const borderStyle = "solid";

        if (isChosen && dealerCardNum > -1) {
            const chosenNominal = getNominal(cardNum);
            const dealerNominal = getNominal(dealerCardNum);
            borderWidth = "3px";

            if (chosenNominal > dealerNominal) {
                borderColor = "green";
            } else if (chosenNominal === dealerNominal) {
                borderColor = "gold";
            } else {
                borderColor = "red";
            }
        }

        console.log(borderColor);
        return (
            <Box
                className={classes.card}
                borderColor={borderColor}
                style={{ color: isRed ? "red" : "black", borderWidth, borderStyle }}
                key={cardNum + Math.random()}
            >
                {getFace(cardNum)}{getSuitSymbol(cardNum)}
            </Box>
        );
    }, [classes]);

    const rootRender =
        loading && !!game ? (
            <Preloader absolute={false} />
        ) : (
            <>
                <Box className={classes.closeBtn}>
                    <IconButton onClick={() => history.goBack()}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Box display="flex" alignItems="center">
                    <AccountCircleIcon fontSize="large" />
                    <h2>
                        {t("ru.spins.subtitle")} #{spin?.id}
                    </h2>
                </Box>
                <br />
                <Divider />
                <>
                    <List>
                        <div className={classes.root}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Box>
                                        <b>{t("ru.spins.fields.bet")}:</b> {spin?.total_bet}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <b>{t("ru.spins.fields.win")}:</b> {spin?.win}
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <b>{t("ru.spins.fields.lines")}:</b> {spin?.lines}
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <b>{t("ru.spins.fields.balance_start")}:</b> {prettyNum(spin?.balance_start)}
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <b>{t("ru.spins.fields.balance_end")}:</b> {prettyNum(spin?.balance_end)}
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <Box display="flex" justifyContent="flex-start" alignItems="center" flexDirection="row" component="section">
                                        <b>{t("ru.spins.fields.created_at")}:</b>{" "}
                                        {spin?.created_at && (
                                            <Box display="flex" justifyContent="center" alignItems="center" flexDirection="row" component="section">
                                                <span style={{ margin: "3px" }}>
                                                    {convertUTCDateToLocalDate(spin?.created_at)}
                                                </span>
                                            </Box>
                                        )}
                                    </Box>
                                </Grid>
                                {parseWins?.step && (
                                    <Grid item xs={12} sm={12}>
                                        <b>{t("ru.spins.fields.step")}:</b> {parseWins?.step}
                                    </Grid>
                                )}

                                {/* Отрисовка риск-игры, если есть prev_cards */}
                                {parseWins?.prev_cards && (
                                    <Grid item xs={12} sm={12}>
                                        <Box mt={2} p={2} border="1px solid #eee" borderRadius="8px" bgcolor="#fafafa">
                                            <Typography variant="subtitle2" gutterBottom>
                                                Риск игра
                                            </Typography>
                                            <Box display="flex" alignItems="center">
                                                <Box mr={4} display="flex" flexDirection="column" alignItems="center">
                                                    <Typography variant="caption" color="textSecondary">Дилер</Typography>
                                                    {renderCard(parseWins.prev_cards.card)}
                                                </Box>
                                                <Box display="flex" flexDirection="column" alignItems="center">
                                                    <Typography variant="caption" color="textSecondary">Выбор игрока</Typography>
                                                    <Box display="flex">
                                                        {parseWins.prev_cards.cards.map((c: number, index: number) =>
                                                            renderCard(
                                                                c,
                                                                index + 1 === parseWins.prev_cards.choose,
                                                                parseWins.prev_cards.card
                                                            )
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Grid>
                                )}

                                <Combination game={game} sessionId={session_id} spin={spin} parseWins={parseWins} />

                                {/* Отдельный виджет для карты дилера на верхнем уровне */}
                                {typeof parseWins?.card === "number" && (
                                    <Grid item xs={12} sm={12}>
                                        <Box mt={2} p={2} border="1px solid #eee" borderRadius="8px" bgcolor="#fafafa">
                                            <Typography variant="subtitle2" gutterBottom>
                                                Карта дилера в следующем удвоении
                                            </Typography>
                                            <Box display="flex" alignItems="center">
                                                {renderCard(parseWins.card)}
                                            </Box>
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>
                        </div>
                    </List>
                </>
            </>
        );

    return <Wrapper>{rootRender}</Wrapper>;
};

export default Spin;